var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Log } from '../log';
import { Sutl } from '../sutl';
import * as KayleeBase from './KayleeBase';
import { getRoom } from './KayleeRoom';
import { getRoomApiKeys } from './KayleeRoomApiKeys';
import { getRoomConfig } from './KayleeRoomConfig';
import { getSite } from './KayleeSite';
const log = Log.channel('RoomCopy');
const debug = true;
export function copyRoom(roomId, siteId, keepEquipmentIpAddresses, rePairControllers, copyApiKeys, newRoomName) {
    var _a, _b, _c, _d, _e, _f, _g;
    return __awaiter(this, void 0, void 0, function* () {
        if (debug)
            log.debug(`getting room`);
        const room = yield getRoom(roomId).$loaded();
        if (debug)
            log.debug(`getting site`);
        const site = yield getSite(siteId).$loaded();
        // Add new room.
        if (debug)
            log.debug(`adding room`);
        const newRoomId = yield site.$addRoom(newRoomName !== null && newRoomName !== void 0 ? newRoomName : room.name);
        if (debug)
            log.debug(`getting new room`);
        const newRoom = yield getRoom(newRoomId).$loaded();
        // Copy equipment to new room. We remove the parts of the config that contain references to the
        // previous equipment ids or the current equipment id (e.g., storage references). Then, after
        // the new equipment has been added, we update those parts of the configs with the new
        // equipment ids.
        if (debug)
            log.debug(`getting equipment`);
        const equipment = room.$getEquipment();
        yield equipment.$loaded();
        const equipmentIdMap = {};
        if (debug)
            log.debug(`adding new equipment`);
        yield Promise.all(equipment.map((eq) => __awaiter(this, void 0, void 0, function* () {
            var _h;
            let config = Object.assign({}, ((_h = eq.config) !== null && _h !== void 0 ? _h : {}));
            delete config.channelFavorites;
            delete config.displayMotion;
            delete config.sourceMenu;
            delete config.synchronizedCalls;
            delete config.videoInputs;
            if (Object.keys(config).length === 0)
                config = undefined;
            if (debug)
                log.debug(`adding equipment ${eq.$id}`);
            equipmentIdMap[eq.$id] = yield newRoom.$addEquipment(eq.type, eq.modelNumber, keepEquipmentIpAddresses ? eq.ipAddress : '', eq.username, eq.password, eq.simulate, config);
        })));
        if (debug)
            log.debug(`getting new equipment`);
        const newEquipment = yield newRoom.$getEquipment().$loaded();
        if (debug)
            log.debug(`waiting on equipment config saves`);
        yield Promise.all(equipment.map((eq) => __awaiter(this, void 0, void 0, function* () {
            var _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
            let configChanged = false;
            const newEq = newEquipment.find(neq => neq.$id === equipmentIdMap[eq.$id]);
            if (!newEq) {
                log.error(`couldn't find new equipment for old id = "${eq.$id}"`);
                return;
            }
            if ((_j = eq.config) === null || _j === void 0 ? void 0 : _j.channelFavorites) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const storage = KayleeBase.app.storage();
                if (!('ref' in storage)) {
                    // Jayne uses the firebase admin sdk, which uses a different storage api.
                    // For now, just don't support this on Jayne.
                    log.error('storage undefined, channel favorites will not be copied');
                }
                else {
                    configChanged = true;
                    if (!newEq.config)
                        newEq.config = {};
                    newEq.config.channelFavorites = {};
                    // Use map instead of:
                    // for (const [key, favorite] of Object.entries(eq.config.channelFavorites)) {
                    // We do this to work around SNTY-2079, which we think is a reslut of this
                    // typescript issue:
                    // https://github.com/microsoft/TypeScript/issues/40897
                    yield Promise.all(Object.entries(eq.config.channelFavorites).map(([key, favorite]) => __awaiter(this, void 0, void 0, function* () {
                        const newFavorite = Sutl.deepCopy(favorite);
                        if (newFavorite.image !== undefined) {
                            const oldPath = newFavorite.image.path;
                            const newPathParts = oldPath.split('/');
                            const oldEqId = newPathParts[1];
                            const newEqId = equipmentIdMap[oldEqId];
                            if (newEqId === undefined) {
                                log.error(`couldn't find new equipment id for old id = "${oldEqId}" ` +
                                    `in channel favorite "${key}"`);
                                delete newFavorite.image;
                            }
                            else {
                                newPathParts[1] = newEqId;
                                const newPath = newPathParts.join('/');
                                const oldImageRef = storage.ref(oldPath);
                                const newImageRef = storage.ref(newPath);
                                const oldImageUrl = yield oldImageRef.getDownloadURL();
                                const resp = yield fetch(oldImageUrl);
                                const blob = yield resp.blob();
                                yield newImageRef.put(blob);
                                newFavorite.image.path = newPath;
                            }
                        }
                        if (!newEq.config)
                            newEq.config = {};
                        if (!newEq.config.channelFavorites) {
                            newEq.config.channelFavorites = {};
                        }
                        newEq.config.channelFavorites[key] = newFavorite;
                    })));
                }
            }
            if ((_k = eq.config) === null || _k === void 0 ? void 0 : _k.displayMotion) {
                configChanged = true;
                if (!newEq.config)
                    newEq.config = {};
                newEq.config.displayMotion = {};
                for (const [key, value] of Object.entries(eq.config.displayMotion)) {
                    newEq.config.displayMotion[key] = Sutl.deepCopy(value);
                    newEq.config.displayMotion[key].controls = {};
                    const controls = newEq.config.displayMotion[key].controls;
                    for (const [ckey, cvalue] of Object.entries(value.controls)) {
                        controls[ckey] = Sutl.deepCopy(cvalue);
                        const actions = (_m = (_l = controls[ckey]) === null || _l === void 0 ? void 0 : _l.actions) !== null && _m !== void 0 ? _m : {};
                        for (const [akey, avalue] of Object.entries((_o = cvalue === null || cvalue === void 0 ? void 0 : cvalue.actions) !== null && _o !== void 0 ? _o : {})) {
                            actions[akey] = Sutl.deepCopy(avalue);
                            actions[akey].equipmentId = equipmentIdMap[avalue.equipmentId];
                        }
                    }
                }
            }
            if ((_p = eq.config) === null || _p === void 0 ? void 0 : _p.sourceMenu) {
                configChanged = true;
                if (!newEq.config)
                    newEq.config = {};
                newEq.config.sourceMenu = {};
                for (const [key, value] of Object.entries(eq.config.sourceMenu)) {
                    newEq.config.sourceMenu[key] = {
                        equipmentId: equipmentIdMap[value.equipmentId],
                        index: value.index,
                        videoInputId: value.videoInputId,
                    };
                }
            }
            if ((_q = eq.config) === null || _q === void 0 ? void 0 : _q.synchronizedCalls) {
                configChanged = true;
                if (!newEq.config)
                    newEq.config = {};
                newEq.config.synchronizedCalls = {};
                for (const [key, value] of Object.entries(eq.config.synchronizedCalls)) {
                    if (value === undefined)
                        continue;
                    newEq.config.synchronizedCalls[key] = Object.assign(Object.assign({}, (value.initialSpotlight
                        ? { initialSpotlight: value.initialSpotlight }
                        : undefined)), { parentEquipmentId: equipmentIdMap[value.parentEquipmentId] });
                }
            }
            if ((_r = eq.config) === null || _r === void 0 ? void 0 : _r.videoInputs) {
                configChanged = true;
                if (!newEq.config)
                    newEq.config = {};
                newEq.config.videoInputs = {};
                for (const [key, value] of Object.entries(eq.config.videoInputs)) {
                    if ('source' in value) {
                        newEq.config.videoInputs[key] = Object.assign(Object.assign({}, value), { source: {
                                equipmentId: equipmentIdMap[value.source.equipmentId],
                                videoOutputId: value.source.videoOutputId,
                            } });
                    }
                    else {
                        // shallow copy is okay here
                        newEq.config.videoInputs[key] = Object.assign({}, value);
                    }
                }
            }
            const zoomCameras = (_v = (_u = (_t = (_s = eq.config) === null || _s === void 0 ? void 0 : _s.zoomControlChannels) === null || _t === void 0 ? void 0 : _t.manual) === null || _u === void 0 ? void 0 : _u.cameras) !== null && _v !== void 0 ? _v : {};
            const hasExtControl = Object.values(zoomCameras).find(camera => camera.extControl !== undefined) !==
                undefined;
            if (hasExtControl) {
                configChanged = true;
                if (!newEq.config)
                    newEq.config = {};
                const newZoomCameras = {};
                for (const [key, zoomCamera] of Object.entries(zoomCameras)) {
                    const newZoomCamera = Sutl.deepCopy(zoomCamera);
                    if (newZoomCamera.extControl) {
                        newZoomCamera.extControl.equipmentId =
                            equipmentIdMap[newZoomCamera.extControl.equipmentId];
                    }
                    newZoomCameras[key] = newZoomCamera;
                }
                if (((_y = (_x = (_w = newEq.config) === null || _w === void 0 ? void 0 : _w.zoomControlChannels) === null || _x === void 0 ? void 0 : _x.manual) === null || _y === void 0 ? void 0 : _y.cameras) !== undefined) {
                    newEq.config.zoomControlChannels.manual.cameras = newZoomCameras;
                }
                else {
                    log.error(`no cameras config for Zoom equipment ${eq.$id}`);
                }
            }
            if (configChanged)
                return newEq.$save();
        })));
        // Copy room config.
        if (debug)
            log.debug(`getting room config`);
        const roomConfig = yield getRoomConfig(roomId).$loaded();
        if (debug)
            log.debug(`getting new room config`);
        const newRoomConfig = yield getRoomConfig(newRoomId).$loaded();
        let addedToRoomConfig = false;
        if (debug)
            log.debug(`copying room config`);
        for (const shadeControl of Object.values((_a = roomConfig === null || roomConfig === void 0 ? void 0 : roomConfig.shadeControls) !== null && _a !== void 0 ? _a : {})) {
            let newShadeControl = Sutl.deepCopy(shadeControl);
            if (newShadeControl.type === 'emittedAction') {
                for (const emittedActions of Object.values((_b = newShadeControl.position) !== null && _b !== void 0 ? _b : {})) {
                    for (const emittedAction of Object.values((_c = emittedActions === null || emittedActions === void 0 ? void 0 : emittedActions.actions) !== null && _c !== void 0 ? _c : {})) {
                        emittedAction.equipmentId = equipmentIdMap[emittedAction.equipmentId];
                    }
                }
                for (const emittedActions of Object.values((_d = newShadeControl.tilt) !== null && _d !== void 0 ? _d : {})) {
                    for (const emittedAction of Object.values((_e = emittedActions === null || emittedActions === void 0 ? void 0 : emittedActions.actions) !== null && _e !== void 0 ? _e : {})) {
                        emittedAction.equipmentId = equipmentIdMap[emittedAction.equipmentId];
                    }
                }
            }
            else {
                newShadeControl.equipmentId = equipmentIdMap[newShadeControl.equipmentId];
            }
            if (!newRoomConfig.shadeControls)
                newRoomConfig.shadeControls = {};
            newRoomConfig.shadeControls[Sutl.generateFirebaseId()] = newShadeControl;
            addedToRoomConfig = true;
        }
        for (const shadePreset of Object.values((_f = roomConfig === null || roomConfig === void 0 ? void 0 : roomConfig.shadePresets) !== null && _f !== void 0 ? _f : {})) {
            const newShadePreset = Sutl.deepCopy(shadePreset);
            if ('equipmentId' in newShadePreset) {
                newShadePreset.equipmentId = equipmentIdMap[newShadePreset.equipmentId];
            }
            if (!newRoomConfig.shadePresets)
                newRoomConfig.shadePresets = {};
            newRoomConfig.shadePresets[Sutl.generateFirebaseId()] = newShadePreset;
            addedToRoomConfig = true;
        }
        if (addedToRoomConfig) {
            if (debug)
                log.debug(`saving new room config`);
            yield newRoomConfig.$save();
        }
        // Copy room settings.
        const settings = Sutl.deepCopy((_g = room.settings) !== null && _g !== void 0 ? _g : {}); // deep copy for future-proofing
        delete settings.calendarSource; // special case, handle after save
        // Copy room API keys.
        if (copyApiKeys) {
            if (debug)
                log.debug(`getting room api keys`);
            const apiKeys = yield getRoomApiKeys(roomId).$loaded();
            yield Promise.all(apiKeys.map((apiKey) => __awaiter(this, void 0, void 0, function* () {
                newRoom.$addApiKey({
                    created: apiKey.created,
                    description: apiKey.description,
                }, apiKey.$id);
            })));
        }
        // Only copy company directories if room is moved within the same company.
        if (settings.companyDirectories && room.companyId !== newRoom.companyId) {
            delete settings.companyDirectories;
        }
        if (Object.keys(settings).length > 0) {
            newRoom.settings = settings;
            if (debug)
                log.debug(`saving room settings`);
            yield newRoom.$save();
        }
        // Handle calendar source setting.
        const info = room.$getSelectedCalendarSourceInfo();
        if (info) {
            if (info.type === 'equipment') {
                if (debug)
                    log.debug(`setting equipment calendar`);
                yield newRoom.$selectCalendarSource('equipment', equipmentIdMap[info.id]);
            }
            else if (info.type === 'office365') {
                if (room.companyId === site.companyId) {
                    if (debug)
                        log.debug(`setting office 365 calendar`);
                    yield newRoom.$selectCalendarSource('office365', info.id);
                }
                else {
                    // We can't move an office 365 calendar to a new company because the company-level
                    // authentication won't match.
                    if (debug)
                        log.debug(`not moving office 365 calendar to new company`);
                }
            }
            else {
                log.error(`unhanded calendar source: ${JSON.stringify(info)}`);
            }
        }
        // Re-pair controllers.
        if (rePairControllers) {
            const controllers = yield room.$getControllers().$loaded();
            if (debug)
                log.debug(`re-pairing controllers`);
            yield Promise.all(controllers.map((controller) => __awaiter(this, void 0, void 0, function* () { return controller.$pair(newRoom); })));
        }
        return newRoomId;
    });
}
